import { useState, useEffect } from "react";
import { Modal, ModalBody, Button } from "reactstrap";
import {
  doc,
  getDoc,
  getDocs,
  query,
  collection,
  where,
  updateDoc,
} from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { db, auth } from "../../util/firebase";

import { useTranslation } from "react-i18next";

const WelcomeTutorialModal = () => {
  const [user] = useAuthState(auth);
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const { t } = useTranslation("common");

  const tutorialSteps = [
    {
      image: t("step1Image"),
      text: t("step1Text"),
    },
    {
      image: t("step2Image"),
      text: t("step2Text"),
    },
    {
      image: t("step3Image"),
      text: t("step3Text"),
    },
    {
      image: t("step4Image"),
      text: t("step4Text"),
    },
  ];

  const checkTutorialStatus = async () => {
    try {
      // Avoid repeating this check multiple times per browser session for the same user
      if (user?.uid) {
        const key = `welcomeChecked_${user.uid}`;
        if (sessionStorage.getItem(key)) {
          // TRACE: replaced console.debug with logger
          // eslint-disable-next-line
          import("../../util/logger").then(({ debug }) =>
            debug("WelcomeTutorialModal: check already performed this session")
          );
          return;
        }
      }
    } catch (e) {
      // ignore
    }
    // TRACE: replaced console.debug with logger
    // eslint-disable-next-line
    import("../../util/logger").then(({ debug }) =>
      debug("WelcomeTutorialModal: checkTutorialStatus called", {
        user: user?.uid,
      })
    );
    try {
      // Try to avoid an expensive collection query by checking for a cached user
      // document in localStorage first, then attempting a direct doc read by UID.
      const cached = localStorage.getItem("userDocument");
      if (cached) {
        const cachedObj = JSON.parse(cached);
        // eslint-disable-next-line
        import("../../util/logger").then(({ debug }) =>
          debug("WelcomeTutorialModal: found cached userDocument", cachedObj)
        );
        if (!cachedObj.userHasFinishedWelcomeTutorial) {
          setShowTutorial(true);
          return;
        }
      }

      // If users are stored with their uid as the document id, this is a fast getDoc.
      if (user?.uid) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap && userSnap.exists()) {
            const data = userSnap.data();
            // eslint-disable-next-line
            import("../../util/logger").then(({ debug }) =>
              debug("WelcomeTutorialModal: got userSnap via getDoc", data)
            );
            if (!data?.userHasFinishedWelcomeTutorial) {
              setShowTutorial(true);
              return;
            }
          }
        } catch (err) {
          // ignore and fall back to collection query below
          // eslint-disable-next-line
          import("../../util/logger").then(({ debug }) =>
            debug("getDoc by uid failed, falling back to query", err)
          );
        }
      }

      // Fallback: query the users collection by uid (existing behavior)
      // eslint-disable-next-line
      import("../../util/logger").then(({ debug }) =>
        debug("WelcomeTutorialModal: falling back to collection query for user")
      );
      const q = query(collection(db, "users"), where("uid", "==", user?.uid));
      const docs = await getDocs(q);
      const userDoc = docs?.docs[0];
      const data = userDoc?.data();

      // eslint-disable-next-line
      import("../../util/logger").then(({ debug }) =>
        debug("WelcomeTutorialModal: query returned", data)
      );

      if (!data?.userHasFinishedWelcomeTutorial) {
        setShowTutorial(true);
      }
      try {
        if (user?.uid) {
          sessionStorage.setItem(`welcomeChecked_${user.uid}`, "1");
        }
      } catch (e) {
        // ignore
      }
    } catch (err) {
      console.error(err);
    }
  };

  const completeTutorial = async () => {
    try {
      // Fast path: if user documents use uid as the doc id
      if (user?.uid) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap && userSnap.exists()) {
            await updateDoc(userRef, {
              userHasFinishedWelcomeTutorial: true,
            });
            // update cached copy
            try {
              const newDoc = {
                ...(userSnap.data ? userSnap.data() : {}),
                userHasFinishedWelcomeTutorial: true,
              };
              localStorage.setItem("userDocument", JSON.stringify(newDoc));
            } catch (e) {
              // ignore
            }
            setShowTutorial(false);
            return;
          }
        } catch (err) {
          console.debug(
            "getDoc/updateDoc by uid failed, falling back to query",
            err
          );
        }
      }

      // Fallback: query the users collection by uid
      const q = query(collection(db, "users"), where("uid", "==", user?.uid));
      const docs = await getDocs(q);
      const userDoc = docs.docs[0];

      const ref = doc(db, "users", userDoc.id);
      await updateDoc(ref, {
        userHasFinishedWelcomeTutorial: true,
      });

      // update cached copy
      try {
        const cached = localStorage.getItem("userDocument");
        const cachedObj = cached ? JSON.parse(cached) : {};
        const updated = { ...cachedObj, userHasFinishedWelcomeTutorial: true };
        localStorage.setItem("userDocument", JSON.stringify(updated));
      } catch (e) {
        // ignore
      }

      setShowTutorial(false);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (user) {
      checkTutorialStatus();
    }
    // eslint-disable-next-line
  }, [user]);

  return (
    <Modal isOpen={showTutorial} backdrop="static">
      <ModalBody style={{ padding: 0 }}>
        <img
          src={tutorialSteps[currentStep].image}
          alt={`tutorial-step-${currentStep + 1}`}
          style={{
            width: "100%",
            borderRadius: 12,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }}
        />
        <p
          style={{
            textAlign: "center",
            marginTop: "1rem",
            paddingLeft: "1rem",
            paddingRight: "1rem",
          }}
        >
          {tutorialSteps[currentStep].text}
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "1rem",
            padding: "1rem",
          }}
        >
          {currentStep === 0 ? (
            <Button
              onClick={() => setCurrentStep((prev) => prev + 1)}
              className="primaryBtn"
            >
              NEXT
            </Button>
          ) : (
            <>
              <Button
                disabled={currentStep === 0}
                onClick={() => setCurrentStep((prev) => prev - 1)}
                className="secondaryBtn"
                style={{ color: "#005EE1", width: "49%" }}
              >
                PREVIOUS
              </Button>

              {currentStep < tutorialSteps.length - 1 ? (
                <Button
                  onClick={() => setCurrentStep((prev) => prev + 1)}
                  className="primaryBtn"
                  style={{ width: "49%" }}
                >
                  NEXT
                </Button>
              ) : (
                <Button
                  onClick={completeTutorial}
                  className="primaryBtn"
                  style={{ width: "49%" }}
                >
                  FINISH
                </Button>
              )}
            </>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default WelcomeTutorialModal;
